import { atom, } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import { useDebounce } from '../Utils/useDounce';
import { useAllItemReview, useDeleteReview, useReviewApprove } from '../../Query/ItemReviewQuery';
import ReviewToolbar from './ManageRestaurant/ReviewToolbar';
import { getReviewColumns } from './ManageRestaurant/ReviewColumns';
import { useLocation } from 'react-router-dom';
import { useReviewManagementParams } from './useReviewManagementParams';
import { StoreReview } from '../../../client-sdk';
import ReviewViewDialog from './ManageRestaurant/ReviewViewDialog';
import { Typography } from '@mui/material';
import { usePermissions } from '../../../context/PermissionContext';

export interface ChoiceGroupDataType {
    storeType: any;
    choiceGroupNameEnglish: string;
    choiceGroupNameArabic: string;
    minimumLimit: number | null;
    maximumLimit: number | null;
    choiceDetails: any;
    choiceGroupOption: any;
    merchantDetails: any;
    id: any;
}

export const choiceGroupAtom = atom<ChoiceGroupDataType | null>(null);

const ManageRestaurantReview = () => {

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);
    const { hasPermission } = usePermissions();
    const { params, dispatch } = useReviewManagementParams();
    const isReady = Boolean(params && Object.keys(params).length > 0);
    const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
    const [currentReview, setCurrentReview] = useState<any>(null);
    const { showSnackbar } = useSnackbar();
    const { showMessage } = useSnackbar();
    const location = useLocation();
    const reviewType = location.state?.reviewType;

    const memoizedParams = useMemo(() => {
        if (!isReady) return {};

        return {
            ...params,
            type: reviewType || params.type // 🔁 use reviewType from location.state if present
        };
    }, [isReady, params, reviewType]);


    const { data: ItemReviewData, isLoading, refetch } = useAllItemReview(memoizedParams);

    const filteredRows = React.useMemo(() => {
        const storeOwners = ItemReviewData?.data || [];

        // Add sno field to each row and ensure all required fields are present
        return storeOwners.map((row, index) => {
            // Ensure we have a valid row object
            if (!row) return

            // Create a new object with all the row properties plus sno
            const rowWithSno = {
                ...row,
                id: (row as StoreReview).reviewId, // type assertion here
                sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),

            };

            return rowWithSno;
        });
    }, [params.search, ItemReviewData, params.page, params.limit]);

    const { mutate: toggleStatus } = useReviewApprove();
    const { mutate: deleteReview } = useDeleteReview();

    const handleCloseDialog = () => setCurrentReview(null);


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    };

    useEffect(() => {
        dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    }, [debouncedSearch]);


    const handleSelectionModelChange = (selection: any[]) =>
        setSelectedReviewIds(selection.map(String));

    const handlePageChange = (newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
    };

    const handlePageSizeChange = (newSize: number) => {
        dispatch({ type: 'SET_LIMIT', payload: newSize });
    };

    const handleEditClick = (row: any) => {
        setCurrentReview(row)
    };

    const handleToggleStatus = (id: string[], isApproved: boolean) => {
        const action = isApproved ? 'approve' : 'disapprove';
        const confirmed = window.confirm(`Are you sure you want to ${action} the selected review(s)?`);
        if (!confirmed) return;
        toggleStatus(
            { id, data: { status: isApproved ? true : false } },
            {
                onSuccess: () => {
                    refetch();
                },
                onError: () => showSnackbar('Failed to update status', 'error'),
            }
        );
    };

    const handleDeleteButton = (ids: string[]) => {
        const confirmed = window.confirm('Are you sure you want to delete the selected review(s)?');
        if (!confirmed) return;
        deleteReview(ids, {
            onSuccess: () => {
                refetch();
                setSelectedReviewIds([])
            },
            onError: () => showSnackbar('Failed to delete review', 'error'),
        });
    };

    const handleBulkAction = (action: 'approve' | 'disapprove' | 'delete') => {
        if (selectedReviewIds.length === 0) {
            showMessage('Please select at least one review to perform this action', 'error');
            return;
        }
        if (action === "approve") {
            handleToggleStatus(selectedReviewIds, true)
        } else if (action === "disapprove") {
            handleToggleStatus(selectedReviewIds, false)
        } else if (action === "delete") {
            handleDeleteButton(selectedReviewIds)
        }

    };

    const columns = getReviewColumns({
        onEdit: handleEditClick,
        onDelete: handleDeleteButton,
        onDisapprove: (id: string[]) => handleToggleStatus(id, false),
        onApprove: (id: string[]) => handleToggleStatus(id, true),
        hasPermission,
        review: "storeReview",
        fields: {
            sno: 'sno',
            storeName: "storeName",
            merchantName: "merchantName",
            customerName: "customerName",
            view: 'View',
            status: 'Status',
            delete: 'Remove',
        },
        labels: {
            sno: 'S.No',
            storeName: "Store Name",
            merchantName: "Merchant Name",
            customerName: "Customer Name",
            view: 'View',
            status: 'Status',
            delete: 'Remove',
            approve: 'Approve',
            disapprove: 'Disapprove',
        },
    });

    return (
        <>
            <Typography variant="h5" fontWeight="bold" mb={2}>
                Manage Restaurant Review
            </Typography>
            <ReviewToolbar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onDisapprove={() => handleBulkAction('disapprove')}
                onApprove={() => handleBulkAction('approve')}
                onDelete={() => handleBulkAction('delete')}
                review="storeReview"
            />

            <CommonDataGrid
                key={`datagrid-page-${params.page}-limit-${params.limit}`}
                rows={filteredRows || []}
                columns={columns}
                pageSize={params.limit}
                onPageSizeChange={handlePageSizeChange}
                page={params.page - 1}
                onPageChange={handlePageChange}
                checkboxSelection
                loading={isLoading}
                rowCount={ItemReviewData?.pagination?.totalItems || 0}
                selectionModel={selectedReviewIds}
                onRowSelectionModelChange={handleSelectionModelChange} components={undefined} />

            <ReviewViewDialog currentReview={currentReview} onClose={handleCloseDialog} />
        </>
    );
};

export default ManageRestaurantReview;
